import * as React from 'react';
import './index.css';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import StatPage from '../StatPage';

import ApiCallLoader from '../../components/ApiCallLoader/ApiCallLoader';

import {
  selectCurrentPage,
  selectApiCallSpinner,
  selectInitialLoad,
  selectPortableMode,
  selectUser,
} from './selectors';

import { checkPortableMode } from './utils';

import ConfirmPage from '../ConfirmPage';
import Header from '../../components/Header/Header';
import PublicHeader from '../../components/PublicHeader/PublicHeader';
import { ToastContainer, Zoom } from 'react-toastify';
import AdminReferralsPage from '../AdminReferralsPage/AdminReferralsPage';
import SubscriptionExpiryWarning from '../../components/SubscriptionExpiryWarning/SubscriptionExpiryWarning';
import useAutopasteExtensionBridge from '../../hooks/useAutopasteExtensionBridge';
import { resetPageZoom } from '../../components/ZoomApp/zoomDocument';
import AdminHomePage from '../AdminHomePage/AdminHomePage';
import ParserErrorsPage from '../ParserErrorsPage/ParserErrorsPage';
import MiningUploadPage from '../MiningUploadPage/MiningUploadPage';
import HomePage from '../HomePage/HomePage';
import RegisterPage from '../RegisterPage/RegisterPage';
import ResetPass from '../ResetPass/ResetPass';

import V2Landing from '../../v2/V2Landing';
import V2Statistics from '../../v2/V2Statistics';
import V2TopPlayers from '../../v2/pages/V2TopPlayers';
import V2Subscription from '../../v2/pages/V2Subscription';
import V2FAQ from '../../v2/pages/V2FAQ';
import V2Referral from '../../v2/pages/V2Referral';
import V2Profile from '../../v2/pages/V2Profile';
import V2Cooperation from '../../v2/pages/V2Cooperation';
import V2Settings from '../../v2/pages/V2Settings';
import V2Register from '../../v2/pages/V2Register';
import V2ResetPass from '../../v2/pages/V2ResetPass';
import V2Users from '../../v2/pages/V2Users';
import V2User from '../../v2/pages/V2User';
import V2AdminReferrals from '../../v2/pages/V2AdminReferrals';
import V2AdminAnalytics from '../../v2/pages/V2AdminAnalytics';
import V2AdminParserErrors from '../../v2/pages/V2AdminParserErrors';
import V2AdminMiningUpload from '../../v2/pages/V2AdminMiningUpload';
import TopPlayersPage from '../TopPlayersPage/TopPlayersPage';
import SettingsPage from '../SettingsPage/SettingsPage';
import SubscriptionPage from '../SubscriptionPage/SubscriptionPage';
import FAQPage from '../FAQPage/FAQPage';
import ReferralPage from '../ReferralPage/ReferralPage';
import CooperationPage from '../CooperationPage/CooperationPage';
import UserProfilePage from '../UserProfilePage/UserProfilePage';
import UsersPage from '../UsersPage/UsersPage';
import UserPage from '../UserPage/UserPage';
import LegacyV2SwitchFooter from '../../components/LegacyV2SwitchFooter/LegacyV2SwitchFooter';
import DesignRoute from '../../components/DesignRoute/DesignRoute';
import AdminRouteShell from '../../components/AdminRouteShell/AdminRouteShell';
import { useDesignVersion } from '../../context/useDesignVersion';

const App = React.memo(() => {
  const currentPage = useSelector(selectCurrentPage);
  const apiCallSpinner = useSelector(selectApiCallSpinner);
  const initialLoad = useSelector(selectInitialLoad);
  const portableMode = useSelector(selectPortableMode);

  const user = useSelector(selectUser);
  const location = useLocation();
  const { isModernDesign, canUseModernDesign } = useDesignVersion();

  useAutopasteExtensionBridge();

  React.useEffect(() => {
    resetPageZoom();
  }, []);

  const showLegacyV2Switch =
    !isModernDesign && canUseModernDesign && !portableMode && location.pathname !== '/confirm';

  return (
    <section className={isModernDesign ? 'App App--v2' : 'App'}>
      <ToastContainer
        className={isModernDesign ? 'v2-toast-container' : undefined}
        autoClose={1500}
        position="top-center"
        transition={Zoom}
        hideProgressBar
        closeButton={false}
      />
      {!checkPortableMode(currentPage, portableMode) && apiCallSpinner && <ApiCallLoader />}

      {!isModernDesign && user && !portableMode && <Header user={user} />}
      {!isModernDesign && !user && !portableMode && <PublicHeader />}
      {!isModernDesign && user && !portableMode && <SubscriptionExpiryWarning />}
      <main className={isModernDesign ? '' : 'App__Main'}>
        {!initialLoad ? (
          <div
            className="app-boot-loader app-boot-loader--overlay"
            role="status"
            aria-live="polite"
          >
            Загрузка…
          </div>
        ) : null}
        <Routes>
          <Route path="/landing" element={<Navigate to="/" replace />} />

          <Route path="/" element={<DesignRoute legacy={<HomePage />} modern={<V2Landing />} />} />
          <Route
            path="/register"
            element={<DesignRoute legacy={<RegisterPage />} modern={<V2Register />} />}
          />
          <Route
            path="/reset"
            element={<DesignRoute legacy={<ResetPass />} modern={<V2ResetPass />} />}
          />
          <Route path="/confirm" element={<ConfirmPage />} />

          <Route
            path="/statistics"
            element={<DesignRoute legacy={<StatPage />} modern={<V2Statistics />} />}
          />
          <Route
            path="/top-players"
            element={<DesignRoute legacy={<TopPlayersPage />} modern={<V2TopPlayers />} />}
          />
          <Route
            path="/subscription"
            element={<DesignRoute legacy={<SubscriptionPage />} modern={<V2Subscription />} />}
          />
          <Route path="/faq" element={<DesignRoute legacy={<FAQPage />} modern={<V2FAQ />} />} />
          <Route path="/FAQ" element={<Navigate to="/faq" replace />} />
          <Route
            path="/referral"
            element={<DesignRoute legacy={<ReferralPage />} modern={<V2Referral />} />}
          />
          <Route
            path="/profile"
            element={<DesignRoute legacy={<UserProfilePage />} modern={<V2Profile />} />}
          />
          <Route
            path="/settings"
            element={<DesignRoute legacy={<SettingsPage />} modern={<V2Settings />} />}
          />
          <Route
            path="/cooperation"
            element={<DesignRoute legacy={<CooperationPage />} modern={<V2Cooperation />} />}
          />
          <Route path="/news" element={<Navigate to="/cooperation" replace />} />

          <Route element={<AdminRouteShell />}>
            <Route
              path="/admin"
              element={<DesignRoute legacy={<AdminHomePage />} modern={<V2AdminAnalytics />} />}
            />
            <Route
              path="/admin/mining-upload"
              element={
                <DesignRoute legacy={<MiningUploadPage />} modern={<V2AdminMiningUpload />} />
              }
            />
            <Route
              path="/admin/parser-errors"
              element={
                <DesignRoute legacy={<ParserErrorsPage />} modern={<V2AdminParserErrors />} />
              }
            />
            <Route
              path="/admin/referrals"
              element={
                <DesignRoute legacy={<AdminReferralsPage />} modern={<V2AdminReferrals />} />
              }
            />
            <Route
              path="/users"
              element={<DesignRoute legacy={<UsersPage />} modern={<V2Users />} />}
            />
            <Route
              path="/users/:id"
              element={<DesignRoute legacy={<UserPage />} modern={<V2User />} />}
            />
          </Route>
        </Routes>
      </main>
      {showLegacyV2Switch ? <LegacyV2SwitchFooter /> : null}
    </section>
  );
});

export default App;
